import { Action } from "redux"
import { all, call, put, takeEvery } from "redux-saga/effects"
import { withCallback } from "redux-saga-callback"

import apiClient from "@api/client"
import { entityTypeFromIModelOrIRI } from "@api/entityTypeEndpointDefinitions"
import { ChallengeTransitionState, IChallenge } from "@api/schema/challenges"
import { IFormikActions } from "@basics/form"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { newSingleEntityUsecaseRequestRunningAction, newSingleEntityUsecaseRequestSuccessAction } from "@redux/common/entityRequest/actions"
import { SingleCLUDApiRequestTrackableEntityType } from "@redux/common/reduxTypes"
import { updateModelSuccessAction } from "@redux/common/scopedObject/actions"
import { SubmissionError } from "@services/submissionError"


/** *************************************************************************************************
 * This enum defines the usecases around the "challenges management".
 */
enum ChallengesForManagementUsecases {
  /**
   * transition challenge
   */
  TransitionChallenge = "_usecase_transition_challenge",
}

// #region transition challenge

interface ITransitionChallengeAction extends Action {
  actions: IFormikActions
  challenge: IChallenge
  transition: ChallengeTransitionState
  type: ChallengesForManagementUsecases.TransitionChallenge
}

export const transitionChallengeAction = (challenge: IChallenge, transition: ChallengeTransitionState, actions: IFormikActions): ITransitionChallengeAction => ({
  actions,
  challenge,
  transition,
  type: ChallengesForManagementUsecases.TransitionChallenge,
})

export function* challengesWatcherSaga(): any {
  yield all([
    takeEvery(ChallengesForManagementUsecases.TransitionChallenge, withCallback(transitionChallengeSaga)),
  ])
}

function* transitionChallengeSaga(action: ITransitionChallengeAction) {
  const { onSuccess, setErrors, setSubmitting } = action.actions || {}

  // special (non-default) sagas for special (non-default) actions use their special usecaseKey (identical to action.type)
  const usecaseKey = action.type // ChallengesForManagementUsecases.TransitionChallenge

  // NOTE cast the return entityType to `SingleCLUDApiRequestTrackableEntityType` since we know
  // that any IChallenge is also request-trackable
  const entityType = entityTypeFromIModelOrIRI<SingleCLUDApiRequestTrackableEntityType>(action.challenge)

  try {
    // yield put(usecaseRequestRunningAction(ScopeTypes.ChallengeOperation))
    yield put(newSingleEntityUsecaseRequestRunningAction(entityType, usecaseKey))

    const challenge: IChallenge = yield call(apiClient.transitionChallenge, action.challenge, action.transition)

    yield put(updateModelSuccessAction(entityType, challenge))

    // yield put(usecaseRequestSuccessAction(ScopeTypes.ChallengeOperation, challenge))
    // yield put(usecaseRequestSuccessAction(usecaseKey, challenge))
    yield put(newSingleEntityUsecaseRequestSuccessAction(entityType, usecaseKey, challenge))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
    if (onSuccess) {
      yield call(onSuccess, challenge)
    }

    return challenge
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR

    if (setErrors) {
      if (err instanceof SubmissionError) {
        // errorHandling: setErrors is a function from FormikHelpers to set errors on a Formik-form
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    // if an error occurred: signalize that the currentScopeType-request has failed with the error message
    // yield put(usecaseRequestRunningAction(ScopeTypes.ChallengeOperation, errorMessage))
    yield put(newSingleEntityUsecaseRequestRunningAction(entityType, usecaseKey, errorMessage))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}

// #endregion